import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const ContactSection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', amount: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    setStatus('sending');
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, val]) => params.append(key, val));
      const res = await fetch('https://readdy.ai/api/form/d7aj03qbdfubkqslan9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) { setStatus('success'); setFormData({ name: '', email: '', phone: '', amount: '', message: '' }); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  const reasons = [
    { icon: 'ri-award-line',             titleKey: 'contact_r1_title', descKey: 'contact_r1_desc' },
    { icon: 'ri-lock-line',              titleKey: 'contact_r2_title', descKey: 'contact_r2_desc' },
    { icon: 'ri-customer-service-2-line',titleKey: 'contact_r3_title', descKey: 'contact_r3_desc' },
    { icon: 'ri-time-line',              titleKey: 'contact_r4_title', descKey: 'contact_r4_desc' },
  ];

  return (
    <section id="contacts" className="py-24 bg-[#0d0d0d] relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img src="https://public.readdy.ai/ai/img_res/fcaa486b273bad8b70b5a1e904908f08.jpg" alt="" className="w-full h-full object-cover object-top opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d] via-[#0d0d0d]/80 to-[#0d0d0d]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-[#d4af37] text-xs tracking-widest uppercase font-medium">{t('contact_badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4 uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {t('contact_title1')}<br />
            <span className="text-[#d4af37]">{t('contact_title2')}</span>
          </h2>
          <p className="text-white/55 text-base max-w-xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>{t('contact_subtitle')}</p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div>
            <div className="mb-10">
              <h3 className="text-xl font-bold text-white mb-6 uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>{t('contact_why_title')}</h3>
              <div className="flex flex-col gap-5">
                {reasons.map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#d4af37]/10 border border-[#d4af37]/20 rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-[#d4af37] text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm mb-0.5">{t(item.titleKey)}</div>
                      <div className="text-white/45 text-xs leading-relaxed">{t(item.descKey)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/3 border border-white/8 rounded-2xl p-6">
              <div className="text-[#d4af37] text-xs tracking-widest uppercase mb-4 font-medium">{t('contact_address_label')}</div>
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-[#d4af37] text-base"></i>
                </div>
                <span className="text-white/60 text-sm leading-relaxed">
                  Rheinmetall AG, Rheinmetall Platz 1,<br />40476 Düsseldorf, Germany
                </span>
              </div>
            </div>
          </div>

          <div>
            <form data-readdy-form onSubmit={handleSubmit} className="bg-[#1a1a1a] border border-white/10 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6 uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>{t('contact_form_title')}</h3>
              <div className="flex flex-col gap-4">
                <div>
                  <label className="text-white/50 text-xs tracking-wider uppercase mb-1.5 block">{t('contact_name_label')}</label>
                  <input type="text" name="name" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t('contact_name_placeholder')} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#d4af37]/50 transition-colors" />
                </div>
                <div>
                  <label className="text-white/50 text-xs tracking-wider uppercase mb-1.5 block">{t('contact_email_label')}</label>
                  <input type="email" name="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@example.com" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#d4af37]/50 transition-colors" />
                </div>
                <div>
                  <label className="text-white/50 text-xs tracking-wider uppercase mb-1.5 block">{t('contact_phone_label')}</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder={t('contact_phone_placeholder')} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#d4af37]/50 transition-colors" />
                </div>
                <div>
                  <label className="text-white/50 text-xs tracking-wider uppercase mb-1.5 block">{t('contact_amount_label')}</label>
                  <select name="amount" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#d4af37]/50 transition-colors cursor-pointer">
                    <option value="" className="bg-[#1a1a1a]">{t('contact_amount_placeholder')}</option>
                    <option value="opt1" className="bg-[#1a1a1a]">{t('contact_amount_opt1')}</option>
                    <option value="opt2" className="bg-[#1a1a1a]">{t('contact_amount_opt2')}</option>
                    <option value="opt3" className="bg-[#1a1a1a]">{t('contact_amount_opt3')}</option>
                    <option value="opt4" className="bg-[#1a1a1a]">{t('contact_amount_opt4')}</option>
                  </select>
                </div>
                <div>
                  <label className="text-white/50 text-xs tracking-wider uppercase mb-1.5 block">{t('contact_msg_label')}</label>
                  <textarea name="message" value={formData.message} onChange={(e) => { if (e.target.value.length <= 500) setFormData({ ...formData, message: e.target.value }); }} placeholder={t('contact_msg_placeholder')} rows={3} maxLength={500} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#d4af37]/50 transition-colors resize-none" />
                  <div className="text-white/30 text-xs text-right mt-1">{formData.message.length}/500</div>
                </div>
                {status === 'success' && <div className="bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3 text-green-400 text-sm text-center">{t('contact_success')}</div>}
                {status === 'error'   && <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm text-center">{t('contact_error')}</div>}
                <button type="submit" disabled={status === 'sending'} className="w-full bg-[#d4af37] text-black font-bold py-4 rounded-xl hover:bg-[#e8c84a] transition-all duration-200 cursor-pointer text-sm tracking-widest uppercase whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">
                  {status === 'sending' ? t('contact_sending') : t('contact_submit')}
                </button>
                <p className="text-white/25 text-xs text-center">{t('contact_privacy')}</p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;